import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { isIOS, isAndroid, isNative } from '../lib/platform';
import { PRODUCT_TO_IAP } from '../lib/iap/products';
import { haptics } from '../lib/native/haptics';
import { useAuth } from './useAuth';
import { useCredits } from './useCredits';

export type CheckoutState = 'idle' | 'loading' | 'success' | 'error';

export interface PixPaymentResult {
  paymentId: string;
  pixCode: string;
  pixQrBase64: string;
  expiresAt: string;
}

interface UseCheckoutOptions {
  onSuccess?: (productId: string) => void;
  onError?: (message: string) => void;
}

export function useCheckout({ onSuccess, onError }: UseCheckoutOptions = {}) {
  const { user, refreshProfile } = useAuth();
  const { refresh: refreshCredits } = useCredits();
  const [state, setState] = useState<CheckoutState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function checkout(productId: string, couponCode?: string) {
    if (!user) { onError?.('Faça login para continuar'); return; }
    setState('loading');
    setErrorMessage(null);

    try {
      // ── Native iOS: Apple IAP ────────────────────────────────────────
      if (isNative && isIOS) {
        await handleAppleIAP(productId);
        return;
      }

      // ── Native Android: Google Play ──────────────────────────────────
      if (isNative && isAndroid) {
        await handleGoogleIAP(productId);
        return;
      }

      // ── Web: Stripe Checkout redirect ─────────────────────────────────
      await handleStripeCheckout(productId, couponCode);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao processar pagamento';
      setState('error');
      setErrorMessage(msg);
      onError?.(msg);
    }
  }

  async function handleStripeCheckout(productId: string, couponCode?: string) {
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        productId,
        couponCode,
        successUrl: `${window.location.origin}/checkout/success?product=${productId}`,
        cancelUrl:  window.location.href,
      },
    });

    if (error || !data?.data?.url) {
      throw new Error('Não foi possível iniciar o pagamento. Tente novamente.');
    }

    // Redirect to Stripe Checkout
    window.location.href = data.data.url;
  }

  async function handleAppleIAP(productId: string) {
    const iapId = PRODUCT_TO_IAP[productId];
    if (!iapId) throw new Error('Produto não disponível na App Store');

    // Dynamically import @capacitor/purchases (RevenueCat) or native bridge
    // For now we surface a message, full IAP requires RevenueCat SDK
    try {
      // Attempt RevenueCat if available
      const { Purchases } = await import('@revenuecat/purchases-capacitor' as never) as unknown as {
        Purchases: {
          purchasePackage: (opts: { aPackage: { identifier: string } }) => Promise<{ transaction: { transactionIdentifier: string } }>;
        }
      };
      const txn = await Purchases.purchasePackage({ aPackage: { identifier: iapId } });
      await verifyAndGrantIAP('ios', txn.transaction.transactionIdentifier, productId);
    } catch {
      throw new Error('Compra não concluída. Tente novamente.');
    }
  }

  async function handleGoogleIAP(productId: string) {
    const iapId = PRODUCT_TO_IAP[productId];
    if (!iapId) throw new Error('Produto não disponível na Play Store');

    try {
      const { Purchases } = await import('@revenuecat/purchases-capacitor' as never) as unknown as {
        Purchases: {
          purchasePackage: (opts: { aPackage: { identifier: string } }) => Promise<{ transaction: { transactionIdentifier: string } }>;
        }
      };
      const txn = await Purchases.purchasePackage({ aPackage: { identifier: iapId } });
      await verifyAndGrantIAP('android', txn.transaction.transactionIdentifier, productId);
    } catch {
      throw new Error('Compra não concluída. Tente novamente.');
    }
  }

  async function verifyAndGrantIAP(platform: 'ios' | 'android', receiptData: string, productId: string) {
    const { data, error } = await supabase.functions.invoke('verify-iap-receipt', {
      body: { platform, receiptData, productId },
    });

    if (error || !data?.data?.verified) {
      throw new Error('Verificação da compra falhou. Entre em contato com o suporte.');
    }

    await refreshProfile();
    await refreshCredits();
    await haptics.success();
    setState('success');
    onSuccess?.(productId);
  }

  /** PIX checkout, returns payment data to display QR code */
  async function startPixCheckout(productId: string, couponCode?: string): Promise<PixPaymentResult> {
    if (!user) throw new Error('Faça login para continuar');

    const { data, error } = await supabase.functions.invoke('create-pix-payment', {
      body: { productId, couponCode },
    });

    if (error || !data?.data?.pixCode) {
      throw new Error('Não foi possível gerar o PIX. Tente novamente.');
    }

    return data.data as PixPaymentResult;
  }

  /** Direct access grant (price = 0 products) */
  async function grantDirectAccess(productId: string) {
    setState('loading');
    // Grant via free product edge fn or directly update credits/subscription
    const { data } = await supabase.functions.invoke('grant-gift-card', {
      body: { code: `FREE_${productId.toUpperCase()}` },
    });
    if (data?.data?.granted) {
      await refreshProfile();
      await refreshCredits();
      setState('success');
      onSuccess?.(productId);
    } else {
      setState('error');
      setErrorMessage('Não foi possível ativar o acesso gratuito.');
    }
  }

  return { checkout, grantDirectAccess, startPixCheckout, state, errorMessage };
}
